import { FormApi } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import { Modal } from "src/ui/shared/Modal/Modal";

import { usePolicyCommands } from "@dashboard/devices/components/Commands/hooks";
import { ControlledJsonTextarea } from "@dashboard/shared/components/JsonTextarea";
import { MdmPolicyCommand } from "@devices/types";

interface Props {
    groupId: number;
    policyCommand: MdmPolicyCommand;
    policyCommands: MdmPolicyCommand[];
    isOpen: boolean;
    toggleDialog: VoidFunction;
}

type FormValues = {
    command: MdmPolicyCommand;
};

export const EditPolicyCommandDialog = (props: Props) => {
    const [error, setError] = React.useState<string | null>(null);
    const { updatePolicyCommands } = usePolicyCommands();
    const { t } = useTranslation(["modals", "common"]);

    const onSubmit = async (values: FormValues) => {
        const updatedPolicyCommands = props.policyCommands.map((command) => {
            return command === props.policyCommand ? values.command : command;
        });

        await updatePolicyCommands({
            groupId: props.groupId,
            policy: JSON.stringify(updatedPolicyCommands),
            onSuccess: props.toggleDialog,
        });
    };

    const onChange = (value: string, form: FormApi<FormValues, Partial<FormValues>>) => {
        try {
            const command: MdmPolicyCommand = JSON.parse(value);
            form.restart({ command });
            setError(null);
        } catch (_error) {
            setError(t("modals:editPolicyCommand.toast.invalidJsonFormat"));
        }
    };

    return (
        <Form<FormValues>
            validateOnBlur
            onSubmit={onSubmit}
            initialValues={{ command: props.policyCommand }}
            render={({ handleSubmit, submitting, values, form }) => (
                <form onSubmit={handleSubmit} id="policyCommand">
                    <Modal
                        title={t("modals:editPolicyCommand.title")}
                        open={props.isOpen}
                        onClose={props.toggleDialog}
                        endButton={
                            <PrimaryButton
                                onClick={form.submit}
                                form="policyCommand"
                                loading={submitting}
                                disabled={submitting || !!error}
                                tooltipProps={{ title: "Please remove validation errors.", hide: !error }}
                            >
                                {t("common:button.save")}
                            </PrimaryButton>
                        }
                    >
                        <ControlledJsonTextarea<FormValues>
                            defaultValue={values?.command}
                            error={error}
                            onChange={onChange}
                            disableResize
                            note={t("modals:editPolicyCommand.note")}
                        />
                    </Modal>
                </form>
            )}
        />
    );
};
